import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	type ExtensionManifest,
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import {
	DEFAULT_ALIGNMENT,
	type NativeEmbedParameterValues,
	setParameters,
} from '@atlaskit/native-embeds-common';
import { fg } from '@atlaskit/platform-feature-flags';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types';
import { DBExperienceManifest } from '@atlassian/native-embeds-db-experience';
import {
	MauiExperienceManifest,
	MAUI_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-maui-experience';
import { PageExperienceManifest } from '@atlassian/native-embeds-page-experience';
import { SlideExperienceManifest } from '@atlassian/native-embeds-slide-experience';
import {
	WhiteboardExperienceManifest,
	WHITEBOARD_EXPERIENCE_MANIFEST_ID,
} from '@atlassian/native-embeds-whiteboard-experience';

const allExperiences = [
	WhiteboardExperienceManifest,
	DBExperienceManifest,
	MauiExperienceManifest,
	PageExperienceManifest,
	SlideExperienceManifest,
] satisfies [ExperienceManifest, ...ExperienceManifest[]];

/**
 * Temporary gating logic to enable/disable MaUI specifically, and whitboards diagrams built from the MaUI flow.
 * To keep logic in one place, all url matching transformations are applied to the experience manifests here.
 */
const gatedExperiences = allExperiences.map((experience: ExperienceManifest) => ({
	...experience,
	isUrlMatch: (url: string): boolean => {
		if (!fg('platform_native_embeds_rollout_non_maui_experience')) {
			// Enable MaUI experience only
			if (experience.id === MAUI_EXPERIENCE_MANIFEST_ID) {
				return experience.isUrlMatch(url);
			}
			// Enable whiteboard experience only if it's a remix diagram
			if (experience.id === WHITEBOARD_EXPERIENCE_MANIFEST_ID) {
				try {
					return (
						experience.isUrlMatch(url) && new URL(url).searchParams.get('isRemixDiagram') === 'true'
					);
				} catch {
					return false;
				}
			}
			return false;
		}
		return experience.isUrlMatch(url);
	},
})) as [ExperienceManifest, ...ExperienceManifest[]];

// Register all experience manifests at module-load time.
// This is intentionally module-level (not inside a React lifecycle) because the
// facade must be available before any child embed loads.
getNativeEmbedFacade().setup({
	experiences: gatedExperiences,
});

const buildNativeEmbedParameters = (
	url: string,
	parameterDefaults?: Partial<NativeEmbedParameterValues>,
) =>
	setParameters(
		{},
		{
			url,
			alwaysShowTitle: false,
			alignment: DEFAULT_ALIGNMENT,
			height: 600,
			width: undefined,
			...parameterDefaults,
		},
	);

const manifest: ExtensionManifest = {
	title: 'Native Embed',
	type: NATIVE_EMBED_EXTENSION_TYPE,
	key: NATIVE_EMBED_EXTENSION_KEY,
	description: 'Embed native Atlassian content',
	modules: {
		nodes: {
			default: {
				type: 'extension',
				render: () =>
					import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
				// @ts-expect-error This option is experimental and not reflected in the manifest type definition
				__hideFrame: true,
			},
			...Object.fromEntries(
				getNativeEmbedFacade()
					.getAllExperienceIds()
					.map((experienceId) => [
						experienceId,
						{
							type: 'extension',
							render: () =>
								import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
							__hideFrame: true,
						},
					]),
			),
		},
		autoConvert: {
			url: [
				(url: string): ADFEntity | undefined => {
					// Check if URL matches any registered experience patterns
					const experienceId = getNativeEmbedFacade().getExperienceIdFromUrl(url);
					if (experienceId) {
						// Check if this experience should auto-convert to native embed
						const manifest = getNativeEmbedFacade().getManifestByUrl(url);
						if (!manifest || manifest.shouldAutoConvertToNativeEmbed !== true) {
							// Return undefined to allow smart link handling instead
							return undefined;
						}

						return {
							type: 'extension',
							attrs: {
								extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${experienceId}`,
								extensionType: NATIVE_EMBED_EXTENSION_TYPE,
								parameters: buildNativeEmbedParameters(url, manifest?.parameterDefaults),
							},
						};
					}

					// Return undefined if URL doesn't match - handled by smart links instead.
					return undefined;
				},
			],
		},
	},
};

export default manifest;
